import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * Country Modal Dialog Component
 * 
 * Manages the display of country-specific product modals with:
 * - Automatic display on first pageview
 * - SessionStorage-based frequency capping
 * - Click-outside and ESC key closing
 * - Smooth animations
 * 
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLButtonElement} closeButton - The close button
 * 
 * @extends Component<Refs>
 */
export class CountryModalDialog extends Component {
  requiredRefs = ['dialog'];

  /** @type {string} */
  #storageKeyBase = 'country_modal_shown';

  /** @type {string} */
  #country = '';

  /** @type {boolean} */
  #debugMode = false;

  connectedCallback() {
    console.log('[Country Modal] connectedCallback called');
    console.log('[Country Modal] Component element:', this);
    console.log('[Country Modal] Dataset:', this.dataset);

    const dialogElement = document.querySelector('.country-modal[ref="dialog"]');
    console.log('[Country Modal] Dialog element found:', dialogElement);

    if (!dialogElement) {
      console.warn('[Country Modal] Dialog element NOT FOUND - modal will not be shown');
      console.log('[Country Modal] Current country from dataset:', this.dataset.country || 'UNKNOWN');
      console.log('[Country Modal] Searched for selector: .country-modal[ref="dialog"]');
      console.log('[Country Modal] All elements with country-modal class:', document.querySelectorAll('.country-modal'));
      return;
    }

    console.log('[Country Modal] Dialog element found, proceeding with initialization');
    super.connectedCallback();

    // Get country code and debug mode from data attributes
    this.#country = this.dataset.country || '';
    this.#debugMode = this.dataset.debugMode === 'true';

    console.log('[Country Modal] Country code:', this.#country);
    console.log('[Country Modal] Debug mode:', this.#debugMode);
    console.log('[Country Modal] Storage key will be:', this.#getStorageKey());

    // Log country info when debug mode is enabled
    if (this.#debugMode) {
      console.log('[Country Modal Debug] Current country ISO code:', this.#country);
      console.log('[Country Modal Debug] Storage key:', this.#getStorageKey());
      console.log('[Country Modal Debug] Debug mode enabled - modal will always show');
    }

    // Check if modal should be shown
    const shouldShow = this.#shouldShowModal();
    console.log('[Country Modal] Should show modal?', shouldShow);

    if (shouldShow) {
      console.log('[Country Modal] Scheduling modal to show in 300ms');
      // Small delay to ensure page is loaded
      requestAnimationFrame(() => {
        setTimeout(() => {
          console.log('[Country Modal] Timeout reached, calling showDialog()');
          this.showDialog();
        }, 300);
      });
    } else {
      console.log('[Country Modal] Modal will NOT be shown (already shown in this session or other condition)');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
  }

  /**
   * Get country-specific storage key
   * @returns {string}
   */
  #getStorageKey() {
    return `${this.#storageKeyBase}_${this.#country.toLowerCase()}`;
  }

  /**
   * Check if modal should be shown based on sessionStorage
   * In debug mode, always return true to bypass sessionStorage
   * @returns {boolean}
   */
  #shouldShowModal() {
    console.log('[Country Modal] Checking if modal should be shown...');

    // Debug mode bypasses sessionStorage check
    if (this.#debugMode) {
      console.log('[Country Modal] Debug mode is ON - bypassing sessionStorage check, will show modal');
      return true;
    }

    try {
      const storageKey = this.#getStorageKey();
      console.log('[Country Modal] Checking sessionStorage with key:', storageKey);

      const shown = sessionStorage.getItem(storageKey);
      console.log('[Country Modal] SessionStorage value:', shown);
      console.log('[Country Modal] Modal already shown?', !!shown);
      console.log('[Country Modal] Will show modal?', !shown);

      return !shown;
    } catch (error) {
      // If sessionStorage is not available, don't show modal
      console.error('[Country Modal] SessionStorage not available:', error);
      return false;
    }
  }

  /**
   * Mark modal as shown in sessionStorage for this specific country
   */
  #markAsShown() {
    try {
      const storageKey = this.#getStorageKey();
      sessionStorage.setItem(storageKey, 'true');
    } catch (error) {
      console.warn('Could not save to sessionStorage:', error);
    }
  }

  /**
   * Shows the dialog
   */
  showDialog() {
    console.log('[Country Modal] showDialog() called');
    console.log('[Country Modal] Refs object:', this.refs);

    const { dialog } = this.refs;
    console.log('[Country Modal] Dialog element from refs:', dialog);

    if (dialog.open) {
      console.log('[Country Modal] Dialog is already open, skipping');
      return;
    }

    console.log('[Country Modal] Opening dialog...');

    // Prevent body scroll
    document.body.style.width = '100%';
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    console.log('[Country Modal] Body scroll locked at position:', scrollY);

    dialog.showModal();
    console.log('[Country Modal] dialog.showModal() called');
    console.log('[Country Modal] Dialog open state:', dialog.open);

    // Mark as shown immediately when opened
    this.#markAsShown();
    console.log('[Country Modal] Marked as shown in sessionStorage');

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('country-modal:open', {
      bubbles: true,
      detail: { country: this.#country }
    }));
    console.log('[Country Modal] Dispatched country-modal:open event for country:', this.#country);

    // Wait until next tick to add event listeners
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
      console.log('[Country Modal] Event listeners attached');
    });
  }

  /**
   * Closes the dialog
   */
  closeDialog = async () => {
    console.log('[Country Modal] closeDialog() called');
    const { dialog } = this.refs;

    if (!dialog.open) {
      console.log('[Country Modal] Dialog is not open, skipping close');
      return;
    }

    console.log('[Country Modal] Closing dialog...');
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');
    console.log('[Country Modal] Added dialog-closing class, waiting for animation');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    console.log('[Country Modal] Animation ended, restoring scroll');

    // Restore body scroll
    document.body.style.width = '';
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: parseInt(scrollY) * -1, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    console.log('[Country Modal] Dialog closed');
    console.log('[Country Modal] Dialog open state after close:', dialog.open);
    console.log('[Country Modal] Dialog display style:', window.getComputedStyle(dialog).display);
    console.log('[Country Modal] Dialog visibility:', window.getComputedStyle(dialog).visibility);
    console.log('[Country Modal] Dialog classList:', dialog.classList.toString());

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('country-modal:close', {
      bubbles: true,
      detail: { country: this.#country }
    }));
    console.log('[Country Modal] Dispatched country-modal:close event');
  };

  /**
   * Handle click events - close on backdrop click
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const { dialog } = this.refs;

    // Check if click is on the backdrop (outside dialog content)
    if (event.target === dialog) {
      this.closeDialog();
    }
  };

  /**
   * Handle keyboard events - close on ESC
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDialog();
    }
  };
}

// Register the custom element
if (!customElements.get('country-modal-dialog')) {
  customElements.define('country-modal-dialog', CountryModalDialog);
}